import { PropsWithChildren, useCallback } from 'react';

import { Colors, typographyStyle, Flex, Button, ArrowBackLine } from '@revenuemarket/cds';
import { overlays } from '@revenuemarket/overlays';
import { Typography } from 'antd';

import { CompanyUserPermission } from '~apis/role/type';
import { PermissionGuard } from '~components/Role/PermissionGuard';
import EditConfirmModal from '~components/ui/Modal/EditConfirmModal';
import Layout from '~components/ui/layout';
import { useGetTaxAgent } from '~hooks/queries/setting/query';
import useRolePermissionStore from '~store/rolePermissionStore';

export type SettingType = 'role' | 'company' | 'tax-agent' | 'member' | 'me';
export const SETTINGS_SIDEBAR_WIDTH = 220;

const LayoutSettings = ({
  children,
  type,
  setType,
  onBack,
}: PropsWithChildren & {
  type: SettingType;
  setType: (type: SettingType) => void;
  onBack: () => void;
}) => {
  const editRoleDetailData = useRolePermissionStore((state) => state.editRoleDetailData);
  const taxAgentData = useGetTaxAgent().data?.Data?.data;

  const handleBack = useCallback(() => {
    if (editRoleDetailData?.isChanged) {
      overlays.open(({ isOpen, onClose }) => (
        <EditConfirmModal
          isOpen={isOpen}
          onCancel={onClose}
          onExit={() => {
            onClose();
            onBack();
          }}
          onConfirm={() => {
            onClose();
          }}
        />
      ));
    } else {
      onBack();
    }
  }, [editRoleDetailData?.isChanged, onBack]);

  const handleMenuClick = useCallback(
    (type: SettingType) => {
      if (editRoleDetailData?.isChanged) {
        overlays.open(({ isOpen, onClose }) => (
          <EditConfirmModal
            isOpen={isOpen}
            onCancel={onClose}
            onExit={() => {
              onClose();
              setType(type);
            }}
            onConfirm={() => {
              onClose();
            }}
          />
        ));
      } else {
        setType(type);
      }
    },
    [editRoleDetailData?.isChanged, setType, onBack],
  );

  const SettingsSidebar = () => {
    return (
      <aside
        style={{
          borderRight: `1px solid ${Colors.grayOpacity07}`,
          backgroundColor: Colors.gray50,
          width: SETTINGS_SIDEBAR_WIDTH,
          height: '100vh',
          position: 'relative',
        }}
      >
        <Flex
          direction="column"
          style={{
            height: '100%',
            padding: '12px 0 12px 12px',
            position: 'sticky',
            top: 0,
            left: 0,
            width: '200px',
          }}
        >
          <Button
            icon={<ArrowBackLine />}
            border
            style={{
              width: 'fit-content',
            }}
            onClick={handleBack}
          >
            뒤로가기
          </Button>
          <Flex style={{ padding: '16px 0 8px 8px' }}>
            <Typography.Text style={{ ...typographyStyle['14_600'], color: Colors.gray800 }}>
              설정
            </Typography.Text>
          </Flex>
          <Flex style={{ padding: '16px 0 8px 8px' }}>
            <Typography.Text style={{ ...typographyStyle['14_600'], color: Colors.gray400 }}>
              계정
            </Typography.Text>
          </Flex>
          <Flex
            style={{
              height: '32px',
              padding: '0px 8px',
              alignItems: 'center',
              cursor: 'pointer',
            }}
          >
            <Typography.Text
              style={{
                ...(type === 'me' ? typographyStyle['14_600'] : typographyStyle['14_400']),
                color: Colors.gray700,
              }}
              onClick={() => handleMenuClick('me')}
            >
              계정 설정
            </Typography.Text>
          </Flex>
          <PermissionGuard
            required={[
              CompanyUserPermission.CAN_MANAGE_COMPANY_USERS,
              CompanyUserPermission.CAN_MANAGE_COMPANY,
            ]}
            mode="some"
          >
            <div>
              <Flex style={{ padding: '16px 0 8px 8px' }}>
                <Typography.Text style={{ ...typographyStyle['14_600'], color: Colors.gray400 }}>
                  회사
                </Typography.Text>
              </Flex>
              <PermissionGuard required={CompanyUserPermission.CAN_MANAGE_COMPANY}>
                <Flex
                  style={{
                    height: '32px',
                    padding: '0px 8px',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleMenuClick('company')}
                >
                  <Typography.Text
                    style={{
                      ...(type === 'company'
                        ? typographyStyle['14_600']
                        : typographyStyle['14_400']),
                      color: Colors.gray700,
                    }}
                  >
                    회사 정보
                  </Typography.Text>
                </Flex>
              </PermissionGuard>
              {taxAgentData && (
                <Flex
                  style={{
                    height: '32px',
                    padding: '0px 8px',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleMenuClick('tax-agent')}
                >
                  <Typography.Text
                    style={{
                      ...(type === 'tax-agent'
                        ? typographyStyle['14_600']
                        : typographyStyle['14_400']),
                      color: Colors.gray700,
                    }}
                  >
                    세무대리인
                  </Typography.Text>
                </Flex>
              )}
              <PermissionGuard required={CompanyUserPermission.CAN_MANAGE_COMPANY_USERS}>
                <Flex
                  style={{
                    height: '32px',
                    padding: '0px 8px',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleMenuClick('member')}
                >
                  <Typography.Text
                    style={{
                      ...(type === 'member'
                        ? typographyStyle['14_600']
                        : typographyStyle['14_400']),
                      color: Colors.gray700,
                    }}
                  >
                    구성원 정보
                  </Typography.Text>
                </Flex>
              </PermissionGuard>
              <PermissionGuard required={CompanyUserPermission.CAN_MANAGE_COMPANY_USERS}>
                <Flex
                  style={{
                    height: '32px',
                    padding: '0px 8px',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleMenuClick('role')}
                >
                  <Typography.Text
                    style={{
                      ...(type === 'role' ? typographyStyle['14_600'] : typographyStyle['14_400']),
                      color: Colors.gray700,
                    }}
                  >
                    역할 관리
                  </Typography.Text>
                </Flex>
              </PermissionGuard>{' '}
            </div>
          </PermissionGuard>
        </Flex>
      </aside>
    );
  };
  return (
    <Layout
      leftSlot={<SettingsSidebar />}
      hideNav
      layoutStyle={{
        border: '1px solidrgb(23, 22, 22)',
        height: '100vh',
      }}
    >
      <div>{children}</div>
    </Layout>
  );
};

export default LayoutSettings;
